import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getWidgetSummary } from '../lib/api/home';

// GET /api/v1/home/widgets/{code} 호출 공통 훅 (비주얼 리뉴얼, 2026-10-23).
// GenericWidgetContainer와 상단 KPI 띠(KpiStrip)가 같은 위젯 코드를 각자 다시 불러
// 쓰는 구조라 fetch/상태 처리가 겹쳤던 부분만 여기로 추출했다 - 데이터 자체(응답 모양,
// 403 처리 등)는 바꾸지 않는다.
//
// 비주얼 리뉴얼 2단계(2026-10-23) §KPI 중복호출: 1단계에서는 이 훅이 호출될 때마다 매번
// fetch했다 - 같은 화면에서 KpiStrip과 GenericWidgetContainer가 같은 widgetCd를 각자 또
// 불러 위젯당 API 호출이 2번씩 나갔다(알려진 비효율로 1단계 커밋 메시지에 기록해 뒀던 항목).
// 새 백엔드 호출을 늘리지 않고 프론트에서만 고치기 위해 "렌더 트리당 캐시"를 Context로 둔다 -
// DashboardContainer가 WidgetSummaryCacheProvider로 KpiStrip과 위젯 그리드를 함께 감싸면
// 같은 widgetCd는 그 안에서 딱 한 번만 fetch되고, 두 구독자(KPI 타일 + 카드)가 결과를 나눠
// 쓴다. Provider가 없는 컨텍스트(예: 이 훅만 단독으로 쓰는 테스트)에서는 기존처럼 컴포넌트별로
// 바로 fetch하는 동작으로 자동 폴백한다 - 호출부(KpiStrip/GenericWidgetContainer) 코드는
// 바뀌지 않는다.
const WidgetSummaryCacheContext = createContext(null);

const initialState = () => ({ loading: true, forbidden: false, summary: null, error: null });

const resolvedState = (res) => ({ loading: false, forbidden: false, summary: res.data, error: null });

const rejectedState = (err) => (err?.response?.status === 403
  ? { loading: false, forbidden: true, summary: null, error: null }
  : { loading: false, forbidden: false, summary: null, error: err });

/** DashboardContainer가 KpiStrip + 위젯 그리드를 함께 감싸는 Provider - 렌더 트리 하나의
 *  생애주기 동안만 사는 캐시다(로그인/세션 TTL과 무관, 페이지를 새로 열면 비워진다). */
export const WidgetSummaryCacheProvider = ({ children }) => {
  // widgetCd -> { state, listeners: Set<fn>, started: boolean }
  const cacheRef = useRef(new Map());
  return (
    <WidgetSummaryCacheContext.Provider value={cacheRef}>
      {children}
    </WidgetSummaryCacheContext.Provider>
  );
};

const getEntry = (cache, widgetCd) => {
  let entry = cache.get(widgetCd);
  if (!entry) {
    entry = { state: initialState(), listeners: new Set(), started: false };
    cache.set(widgetCd, entry);
  }
  return entry;
};

const notify = (entry) => {
  entry.listeners.forEach((listener) => listener(entry.state));
};

const ensureFetchStarted = (cache, widgetCd) => {
  const entry = getEntry(cache, widgetCd);
  if (entry.started) return entry;
  entry.started = true;
  getWidgetSummary(widgetCd)
    .then((res) => {
      entry.state = resolvedState(res);
      notify(entry);
    })
    .catch((err) => {
      entry.state = rejectedState(err);
      notify(entry);
    });
  return entry;
};

export const useWidgetSummary = (widgetCd) => {
  const cacheRef = useContext(WidgetSummaryCacheContext);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    let active = true;

    if (!cacheRef) {
      // Provider 밖 - 기존(1단계) 동작: 컴포넌트마다 독립적으로 fetch.
      setState(initialState());
      getWidgetSummary(widgetCd)
        .then((res) => { if (active) setState(resolvedState(res)); })
        .catch((err) => { if (active) setState(rejectedState(err)); });
      return () => { active = false; };
    }

    const cache = cacheRef.current;
    const entry = ensureFetchStarted(cache, widgetCd);
    const listener = (next) => { if (active) setState(next); };
    entry.listeners.add(listener);
    setState(entry.state);

    return () => {
      active = false;
      entry.listeners.delete(listener);
    };
  }, [widgetCd, cacheRef]);

  return state;
};
